import './style.less';
import { orderBy, uniq, map, flatten, get } from 'lodash';

export default function($filter) {
  'ngInject';

  const vm = this;
  vm.$onChanges = init;
  let anchorCount = -1;

  function getAnchor() {
    anchorCount++;
    return Number(anchorCount);
  }

  function addAnchor(tiles) {
    return tiles.map(tile => {
      return {
        anchor: getAnchor(),
        ...tile
      };
    });
  }

  function buildTiles(type, regulator, hourCategories, cycleDate, regulatorIds, span_count) {
    if (type !== 'span' && type !== 'category') throw new Error('Invalid Tile Type');

    return Object.keys(hourCategories)
      .map(category => {
        if (type === 'span') {
          if (hourCategories[category].cycle.start_date && hourCategories[category].cycle.end_date) {
            cycleDate = `${$filter('PrettyDate')(hourCategories[category].cycle.start_date)} - ${$filter('PrettyDate')(
              hourCategories[category].cycle.end_date
            )}`;
          } else {
            const regDate = regulator.date;
            const spanLength = hourCategories[category].cycle.cycle || cycleDate;
            cycleDate = span_count
              ? $filter('CycleDuration')(
                  regDate,
                  spanLength,
                  regulator.issue_date,
                  hourCategories[category].cycle.yr_offset
                )
              : $filter('CycleDuration')(regDate, spanLength);
          }
        }

        const tile = {
          type,
          cyc_yr: regulator.cyc_yr,
          text: $filter('CategoryName')(hourCategories[category].name),
          category: hourCategories[category].name,
          subText: cycleDate,
          hoursActual: hourCategories[category].cycle.actual,
          hoursCompleted: hourCategories[category].cycle.completed,
          hourLimit: hourCategories[category].cycle.max,
          certs: hourCategories[category].cycle.cert_ids,
          regulatorIds
        };

        if (span_count) tile.yr_offset = hourCategories[category].yr_offset || 0;

        if (hourCategories[category].cycle.min) {
          tile.type += '-min';
          tile.hoursNeeded = hourCategories[category].cycle.min;
          tile.hoursIncomplete = Math.max(
            0,
            hourCategories[category].cycle.min - hourCategories[category].cycle.completed
          );
          delete tile.hourLimit;
        }
        return tile;
      })
      .sort((a, b) => b.type.length - a.type.length);
  }

  function buildYearTiles(years) {
    const yearsIndex = uniq(map(years, 'year')).sort();

    const yearTiles = Object.keys(years)
      .sort()
      .map(category_yr => {
        const { lic_yr, min, completed, year, cert_ids, incomplete } = years[category_yr];
        const calendarYear = category_yr.substring(category_yr.length - 4);
        const subTextDate = new Date(vm.regulator.date).setFullYear(calendarYear);

        let extension_date = years[category_yr].extension_date;

        if (vm.regulator.extension_date && calendarYear == new Date(vm.regulator.date).getFullYear()) {
          extension_date = vm.regulator.extension_date;
        }

        const yearIndex = yearsIndex.indexOf(year) + 1;
        const subText = $filter('CycleDuration')(subTextDate, 1, vm.regulator.issue_date, false, extension_date);

        return {
          type: 'year',
          lic_yr,
          year,
          text: `Annual ${$filter('CategoryName')(years[category_yr].category)} - Year ${yearIndex}`,
          subText,
          hoursActual: completed,
          hoursCompleted: completed,
          hoursNeeded: min,
          hoursIncomplete: incomplete,
          regulatorIds: [lic_yr],
          certs: cert_ids
        };
      });
    return orderBy(yearTiles, ['text'], ['asc']);
  }

  function init() {
    try {
      if (!vm.regulator) {
        return;
      }

      anchorCount = -1;
      const cycleDate = $filter('CycleDuration')(
        vm.regulator.date,
        vm.regulator.cycleYears,
        vm.regulator.issue_date,
        false,
        vm.regulator.extension_date
      );

      vm.regulator.cycleDate = cycleDate;

      const regulatorIds = [];
      const categoryTiles = [];
      const spanTiles = [];

      const certs = uniq(flatten(map(vm.regulator.hour_categories, 'cycle.cert_ids')));

      Object.keys(vm.regulator.annuals).forEach(year => {
        regulatorIds.push(vm.regulator.annuals[year].lic_yr);
      });

      const cycleTile = {
        type: 'cycle',
        text: 'Overall Total',
        subText: cycleDate,
        hoursActual: vm.regulator.cycleHours.actual,
        hoursCompleted: vm.regulator.cycleHours.completed,
        hoursNeeded: vm.regulator.cycleHours.totalHours,
        hoursIncomplete: get(vm.regulator, 'applied_incomplete.total', 0),
        regulatorIds,
        certs
      };

      const years = vm.regulator.annuals;
      const yearTiles = buildYearTiles(years);

      const hourCategories = vm.regulator.hour_categories;

      buildTiles('category', vm.regulator, hourCategories, cycleDate, regulatorIds).forEach(tile => {
        if (tile.category && tile.category.startsWith('A & A')) return spanTiles.push(tile);
        categoryTiles.push(tile);
      });

      if (vm.regulator.span) {
        spanTiles.push(...buildTiles('span', vm.regulator, vm.regulator.span, vm.regulator.cycleYears, regulatorIds));
      }

      if (vm.regulator.span_count) {
        const spanCountTiles = buildTiles(
          'span',
          vm.regulator,
          vm.regulator.span_count,
          vm.regulator.cycleYears,
          regulatorIds,
          true
        );
        spanTiles.push(...spanCountTiles);
      }

      if (vm.regulator.carry_over_applied && vm.regulator.carry_over_accrued) {
        const carry_over_applied = Object.values(vm.regulator.carry_over_applied.total || {}).reduce((a, b) => a + b);
        const carry_over_accrued = Object.values(vm.regulator.carry_over_accrued.total || {}).reduce((a, b) => a + b);

        vm.regulator.show_carry_over = !!(carry_over_applied || carry_over_accrued);
      }

      vm.reportParams = {
        profile_uids: vm.profile ? [vm.profile.profile_uid] : undefined,
        reg_ids: [vm.regulator.reg_id],
        rangeEnd: vm.regulator.extension_date || vm.regulator.date,
        rangeStart: $filter('RangeDate')(vm.regulator.date, vm.regulator.cycleYears, 1)
      };

      vm.caRenewalReportParams = {
        cyc_yr: vm.regulator.cyc_yr,
        profile_uid: vm.profile ? vm.profile.profile_uid : undefined,
        reg_ids: [vm.regulator.reg_id],
        rangeEnd: vm.regulator.extension_date || vm.regulator.date,
        rangeStart: $filter('RangeDate')(vm.regulator.date, vm.regulator.cycleYears, 1)
      };

      vm.overallRequirementTiles = [cycleTile];
      vm.annualRequirementTiles = [...yearTiles];
      vm.subjectAreaTiles = [...categoryTiles];
      vm.spanTiles = orderBy(spanTiles, ['category'], ['asc']);

      vm.overallRequirementTiles = addAnchor(vm.overallRequirementTiles);
      vm.annualRequirementTiles = addAnchor(vm.annualRequirementTiles);
      vm.subjectAreaTiles = addAnchor(vm.subjectAreaTiles);
      vm.spanTiles = addAnchor(vm.spanTiles);

      vm.totalTiles = [
        ...vm.overallRequirementTiles,
        ...vm.annualRequirementTiles,
        ...vm.subjectAreaTiles,
        ...vm.spanTiles
      ];

      if (typeof vm.setCerts() === 'function') {
        vm.setCerts()(vm.totalTiles);
      }
    } catch (error) {
      throw error;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/report/reportNav/controller.js